import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api, { auth } from '../lib/api';

const AuthContext = createContext();

const safeJson = (value, fallback = null) => {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => safeJson(localStorage.getItem('user')));
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [currentOutlet, setCurrentOutlet] = useState(() => safeJson(localStorage.getItem('currentOutlet')));
  const [loading, setLoading] = useState(true);
  const [setupCompleted, setSetupCompleted] = useState(null); // null = unknown

  const refreshSetup = async () => {
    try {
      const res = await api.get('/api/tenant/setup-status');
      setSetupCompleted(!!res.data.completed);
      return res.data;
    } catch {
      setSetupCompleted(true); // fail-open: never lock users out on error
      return null;
    }
  };

  const isPlatformAdmin = !!user?.is_platform_admin || user?.tenant_id === 'platform';
  const roleName = user?.role_name || user?.role || '';
  const isOwner = ['owner', 'store_owner'].includes(roleName);
  const isManager = ['owner', 'store_manager', 'finance_manager', 'manager'].includes(roleName);
  const isCashier = ['cashier'].includes(roleName);

  const persistSession = (accessToken, nextUser) => {
    localStorage.setItem('token', accessToken);
    localStorage.setItem('user', JSON.stringify(nextUser));
    setToken(accessToken);
    setUser(nextUser);
  };

  const clearSession = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentOutlet');
    setToken(null);
    setUser(null);
    setCurrentOutlet(null);
  };

  useEffect(() => {
    const restore = async () => {
      const savedToken = localStorage.getItem('token');
      if (!savedToken) {
        setLoading(false);
        return;
      }

      try {
        const res = await auth.me();
        localStorage.setItem('user', JSON.stringify(res.data));
        setUser(res.data);
        setToken(savedToken);
        if (!res.data?.is_platform_admin) refreshSetup();
      } catch {
        clearSession();
      } finally {
        setLoading(false);
      }
    };

    restore();
  }, []);

  const login = async (email, password, platform = false) => {
    setLoading(true);
    try {
      const endpoint = platform ? '/api/auth/platform/login' : '/api/auth/login';
      const res = await api.post(endpoint, { email, password });
      persistSession(res.data.access_token, res.data.user);
      if (!res.data.user?.is_platform_admin) refreshSetup();
      return res.data.user;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearSession();
  };

  // Session timeout (R15): auto-logout after a period of inactivity. The window
  // (minutes) can be tuned per deployment via REACT_APP_IDLE_MINUTES (default 30).
  useEffect(() => {
    if (!token) return;
    const idleMinutes = Number(process.env.REACT_APP_IDLE_MINUTES) || 30;
    const idleMs = idleMinutes * 60 * 1000;
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        clearSession();
        try { window.location.assign('/login'); } catch { /* noop */ }
      }, idleMs);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, reset));
    };
  }, [token]);

  const selectOutlet = (outlet) => {
    localStorage.setItem('currentOutlet', JSON.stringify(outlet));
    localStorage.setItem('selectedOutletId', outlet.id);
    setCurrentOutlet(outlet);
  };

  const value = useMemo(() => ({
    user,
    token,
    currentOutlet,
    loading,
    login,
    logout,
    selectOutlet,
    isPlatformAdmin,
    isOwner,
    isManager,
    isCashier,
    roleName,
    setupCompleted,
    refreshSetup,
  }), [user, token, currentOutlet, loading, isPlatformAdmin, isOwner, isManager, isCashier, roleName, setupCompleted]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
